/**
 * ResearchConfirmDialog — 3-option pre-flight modal for "Research with AI".
 *
 * Options:
 *   1. "Fill only empty fields" → overwrite_mode: 'fill_only'
 *   2. "Overwrite existing content" → overwrite_mode: 'overwrite'
 *   3. "Cancel" — dismiss without action
 *
 * Visual layout (B35 fix — size="md" so DE labels fit):
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Research with AI                               [×]       │
 *   │                                                          │
 *   │ Choose how the AI should handle existing data.           │
 *   │                                                          │
 *   │  [ Overwrite ]  [ Cancel ]  [ Fill empty fields →]      │
 *   └──────────────────────────────────────────────────────────┘
 *
 * - Fill empty fields: primary (right-most) — safer recommended action.
 * - Overwrite: secondary (left of Cancel) — not danger; overwrite is reversible.
 * - Cancel: ghost (middle) — easy escape.
 *
 * B35 changes:
 *   - size: sm→md (DE labels overflowed in sm)
 *   - Overwrite: danger→secondary (overwrite is reversible; danger was too alarmist)
 *   - Button order: [Overwrite-secondary] [Cancel-ghost] [Fill-primary]
 *   - Modal close × already has aria-label="Close modal" via Modal component
 */

import React from 'react';
import { Modal } from './Modal/Modal';
import { Button } from './Button';
import { t } from '@/lib/i18n';

export interface ResearchConfirmDialogProps {
  isOpen: boolean;
  onFillOnly: () => void;
  onOverwrite: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ResearchConfirmDialog({
  isOpen,
  onFillOnly,
  onOverwrite,
  onCancel,
  isLoading = false,
}: ResearchConfirmDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={t('Research with AI')}
      size="md"
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
      footer={
        <div
          className="flex items-center justify-end gap-2 w-full"
          data-testid="research-confirm-dialog-footer"
        >
          {/* B35: Overwrite first (secondary, not danger — reversible action) */}
          <Button
            variant="secondary"
            onClick={onOverwrite}
            disabled={isLoading}
            data-testid="research-confirm-overwrite"
          >
            {t('Overwrite existing content')}
          </Button>
          {/* Cancel in middle */}
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isLoading}
            data-testid="research-confirm-cancel"
          >
            {t('Cancel')}
          </Button>
          {/* Primary (recommended) action right-anchored */}
          <Button
            variant="primary"
            onClick={onFillOnly}
            isLoading={isLoading}
            data-testid="research-confirm-fill-only"
          >
            {t('Fill only empty fields')}
          </Button>
        </div>
      }
    >
      <p
        className="text-sm text-muted-foreground"
        data-testid="research-confirm-description"
      >
        {t('The AI will crawl your website and suggest data for your company profile. Choose how it should handle existing content.')}
      </p>
    </Modal>
  );
}
